import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, 
  Shield, 
  Bell, 
  Lock, 
  LogOut, 
  ChevronRight,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Minus,
  CreditCard,
  Crown,
  Tag,
  Monitor
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useAuth } from '@/hooks/useAuth';
import { ProfileSection } from './ProfileSection';
import { SecuritySection } from './SecuritySection';
import { NotificationsSection } from './NotificationsSection';
import { PrivacySection } from './PrivacySection';
import { StatsSection } from './StatsSection';
import { TagsSection } from './TagsSection';
import { DesktopSettingsSection } from '@/components/desktop/DesktopSettingsSection';
import { useProfile } from '@/hooks/useProfile';
import { useSubscription } from '@/hooks/useSubscription';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { isMainDesktopWindow, openPaymentUrl } from '@/lib/desktopBridge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

type SectionType = 'main' | 'profile' | 'security' | 'notifications' | 'privacy' | 'stats' | 'tags' | 'desktop';

interface MenuItem {
  id: SectionType;
  icon: React.ElementType;
  label: string;
  description?: string;
}

const menuItems: MenuItem[] = [
  { id: 'profile', icon: User, label: 'Personal Info', description: 'Name and email' },
  { id: 'security', icon: Shield, label: 'Security', description: 'Password and login' },
  { id: 'notifications', icon: Bell, label: 'Notifications', description: 'Email and alerts' },
  { id: 'privacy', icon: Lock, label: 'Privacy', description: 'Data and permissions' },
  { id: 'stats', icon: TrendingUp, label: 'Productivity', description: 'Your progress' },
  { id: 'tags', icon: Tag, label: 'Tags', description: 'Manage your tags' },
];

export function ProfileSheet() {
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const { isProUser, tier, manageSubscription, upgradeToPro } = useSubscription();
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<SectionType>('main');
  const [isManagingSubscription, setIsManagingSubscription] = useState(false);

  const handleSignOut = async () => {
    setIsOpen(false);
    await signOut();
  };

  const handleManageSubscription = async () => {
    setIsManagingSubscription(true);
    try {
      if (isProUser && tier !== 'free_for_life') {
        // Pro user - open customer portal
        const result = await manageSubscription();
        if (result.portalUrl) {
          openPaymentUrl(result.portalUrl);
        } else {
          toast.error(result.error || 'Could not open subscription manager');
        }
      } else {
        // Basic user - upgrade to pro
        const result = await upgradeToPro();
        if (result.checkoutUrl) {
          openPaymentUrl(result.checkoutUrl);
        } else if (result.success) {
          // Native IAP path (iOS / Mac App Store) completes without a URL
          toast.success('Welcome to Luma Pro!');
        } else {
          toast.error(result.error || 'Could not start checkout');
        }
      }
    } finally {
      setIsManagingSubscription(false);
    }
  };

  const swipeToClose = useSwipeToClose(() => setIsOpen(false), 'right');

  const getInitials = () => {
    if (profile?.display_name) {
      return profile.display_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return user?.email?.slice(0, 2).toUpperCase() || 'U';
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'profile':
        return <ProfileSection onBack={() => setActiveSection('main')} />;
      case 'security':
        return <SecuritySection onBack={() => setActiveSection('main')} />;
      case 'notifications':
        return <NotificationsSection onBack={() => setActiveSection('main')} />;
      case 'privacy':
        return <PrivacySection onBack={() => setActiveSection('main')} />;
      case 'stats':
        return <StatsSection onBack={() => setActiveSection('main')} />;
      case 'tags':
        return <TagsSection onBack={() => setActiveSection('main')} />;
      case 'desktop':
        return <DesktopSettingsSection onBack={() => setActiveSection('main')} />;
      default:
        return null;
    }
  };

  // Desktop-only "Desktop" settings entry (menu bar / floating timer prefs)
  const allMenuItems: MenuItem[] = isMainDesktopWindow()
    ? [...menuItems, { id: 'desktop', icon: Monitor, label: 'Desktop', description: 'Menu bar & floating timer' }]
    : menuItems;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => {
      setIsOpen(open);
      if (!open) setActiveSection('main');
    }}>
      <SheetTrigger asChild>
        <button className="flex items-center gap-2 hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-xs font-semibold text-primary-foreground">
              {getInitials()}
            </span>
          </div>
          <span className="font-semibold tracking-tight hidden sm:inline">Weekly</span>
        </button>
      </SheetTrigger>
      
      <SheetContent 
        side="left" 
        className="w-full sm:max-w-md p-0 overflow-hidden"
      >
        <div
          className="h-full touch-pan-y"
          {...swipeToClose}
        >
        <AnimatePresence mode="wait">
          {activeSection === 'main' ? (
            <motion.div
              key="main"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="h-full flex flex-col"
            >
              {/* Header */}
              <div className="p-6 border-b">
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xl font-semibold">
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-semibold truncate">
                      {profile?.display_name || 'Welcome'}
                    </h2>
                    <p className="text-sm text-muted-foreground truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
              </div>

              {/* Single scrollable section: menu, subscription, sign out */}
              <div className="flex-1 min-h-0 overflow-auto p-4 space-y-1">
                {allMenuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    className={cn(
                      "w-full flex items-center gap-4 p-4 rounded-xl",
                      "hover:bg-secondary/80 active:scale-[0.98] transition-all",
                      "focus:outline-none focus:ring-2 focus:ring-primary"
                    )}
                  >
                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center shrink-0">
                      <item.icon className="h-5 w-5 text-foreground" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium">{item.label}</p>
                      {item.description && (
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      )}
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </button>
                ))}

                {/* Subscription Management */}
                <div className="pt-2">
                  <button
                    onClick={handleManageSubscription}
                    disabled={isManagingSubscription || tier === 'free_for_life'}
                    className={cn(
                      "w-full flex items-center gap-4 p-4 rounded-xl mb-2",
                      "hover:bg-secondary/80 active:scale-[0.98] transition-all",
                      "focus:outline-none focus:ring-2 focus:ring-primary",
                      "disabled:opacity-50 disabled:cursor-not-allowed"
                    )}
                  >
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center shrink-0",
                      isProUser ? "bg-primary/20" : "bg-secondary"
                    )}>
                      {isProUser ? (
                        <CreditCard className="h-5 w-5 text-primary" />
                      ) : (
                        <Crown className="h-5 w-5 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium">
                        {tier === 'free_for_life' ? 'Free for Life' : isProUser ? 'Manage Subscription' : 'Upgrade to Pro'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {tier === 'free_for_life' ? 'All Pro features unlocked' : isProUser ? 'Billing & payment' : '$4.99/month'}
                      </p>
                    </div>
                    {tier !== 'free_for_life' && (
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    )}
                  </button>
                </div>

                {/* Sign Out */}
                <div className="pt-2">
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={handleSignOut}
                  >
                    <LogOut className="h-5 w-5" />
                    Sign Out
                  </Button>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              className="h-full"
              drag="x"
              dragDirectionLock
              dragConstraints={{ left: 0, right: 120 }}
              dragElastic={0.15}
              onDragEnd={(_, info) => {
                if (info.offset.x > 60 || info.velocity.x > 300) {
                  setActiveSection('main');
                }
              }}
            >
              {renderSection()}
            </motion.div>
          )}
        </AnimatePresence>
        </div>
      </SheetContent>
    </Sheet>
  );
}
